import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { formatNumber, getSortIndicator } from '../../../../utils/helpers';
import useSortedData from '../../hooks/useSortedData';
import './EntrantsTeamsPokerCompleted.css'; // Import Completed CSS

const EntrantsTeamsPokerCompleted = ({ contest, currentUsername, onTeamSelect }) => {
  // Player data map is needed for captain's cashes tiebreaker
  const playerDataMap = useMemo(() => {
    const map = {};
    contest.players.forEach(player => {
      // Only need cashedFor for tiebreaker logic here
      map[player.name] = { cashedFor: player.cashedFor }; 
    });
    return map;
  }, [contest.players]);

  // Helper function to calculate winnings based on rank and payouts
  const calculateWinnings = useCallback((rank, payouts, allEntrants) => {
    if (!payouts || payouts.length === 0) return 0;
    
    // Find all entrants with the same rank
    const tiedEntrants = allEntrants.filter(e => e.numericRank === rank);
    const numTied = tiedEntrants.length;
    
    // If no one is tied, return the payout directly
    if (numTied === 1) {
      return payouts[rank - 1] || 0;
    }
    
    // For ties, sum up the payouts for all tied positions and divide equally
    let totalPayout = 0;
    for (let i = 0; i < numTied; i++) {
      const payoutIndex = rank - 1 + i;
      if (payoutIndex < payouts.length) {
        totalPayout += payouts[payoutIndex];
      }
    }
    
    return Math.floor(totalPayout / numTied);
  }, []); // No dependencies needed now

  // Pre-process entrants: calculate rank with tiebreakers
  const processedEntrants = useMemo(() => {
    // 1. Calculate cashes for each entrant based on player data
    const entrantsWithCalculatedCashes = contest.entrants.map(entrant => {
      let totalCashes = 0;
      
      // Add captain's cashes
      const captainData = playerDataMap[entrant.team.captain];
      if (captainData) {
        totalCashes += captainData.cashedFor || 0;
      }
      
      // Add flex players' cashes
      entrant.team.flexPlayers.forEach(player => {
        const playerData = playerDataMap[player];
        if (playerData) {
          totalCashes += playerData.cashedFor || 0;
        }
      });
      
      return {
        ...entrant,
        cashes: totalCashes // Override the cashes with calculated value
      };
    });

    // 2. Map entrants to include captain's cashes
    const entrantsWithCaptainCashes = entrantsWithCalculatedCashes.map(entrant => ({
      ...entrant,
      captainCashes: playerDataMap[entrant.team.captain]?.cashedFor || 0
    }));

    // 3. Sort by total cashes (desc), then captain's cashes (desc)
    const sortedEntrants = [...entrantsWithCaptainCashes].sort((a, b) => {
      const cashesDiff = b.cashes - a.cashes;
      if (cashesDiff !== 0) return cashesDiff;
      // Tiebreaker: captain's cashes
      return b.captainCashes - a.captainCashes;
    });

    // 4. Assign numeric rank based on sorted order, handling ties
    let currentRank = 0;
    let previousCashes = null;
    let previousCaptainCashes = null;
    const entrantsWithRank = sortedEntrants.map((entrant, index) => {
      if (entrant.cashes !== previousCashes || entrant.captainCashes !== previousCaptainCashes) {
        currentRank = index + 1;
        previousCashes = entrant.cashes;
        previousCaptainCashes = entrant.captainCashes;
      }
      return { ...entrant, rank: currentRank }; // Assign numeric rank
    });

    // 5. Determine display place (with "T" for ties)
    const entrantsWithPlace = entrantsWithRank.map((entrant, index, arr) => {
      const isTiedWithPrevious = index > 0 && entrant.rank === arr[index - 1].rank;
      const isTiedWithNext = index < arr.length - 1 && entrant.rank === arr[index + 1].rank;
      const placePrefix = isTiedWithPrevious || isTiedWithNext ? 'T' : '';
      return {
        ...entrant,
        place: `${placePrefix}${entrant.rank}`,
        numericRank: entrant.rank,
        displayName: entrant.entryName || entrant.username,
        teamSalary: entrant.teamSalary
      };
    });

    // 6. Add winnings after all other processing is done
    return entrantsWithPlace.map(entrant => ({
      ...entrant,
      winnings: calculateWinnings(entrant.rank, contest.entrantPayouts, entrantsWithPlace)
    }));

  }, [contest.entrants, playerDataMap, calculateWinnings]); // Dependencies

  // Apply sorting hook (initial sort is by final rank/place)
  const { sortedData, requestSort, sortConfig } = useSortedData(
    processedEntrants,
    'numericRank', // Initial sort key is the numeric rank
    'ascending'    // Ranks go 1, 2, T3, T3, 5...
  );

  // Determine number of paying places
  const numPayingPlaces = contest.entrantPayouts?.length || 0;

  return (
    <div className="entrants-teams-container-poker-completed"> 
      <div className="entrants-teams-header">
        <span>Final Standings</span> 
        <span className="header-info-text">(Click team to view)</span>
      </div>
      <div className="entrants-teams-table-container">
        <table className="entrants-teams-table">
          <thead>
            <tr>
              {/* Sort by numericRank when Rank header clicked */}
              <th onClick={() => requestSort('numericRank', true)}>Rank {getSortIndicator(sortConfig, 'numericRank')}</th> 
              <th onClick={() => requestSort('displayName')}>User {getSortIndicator(sortConfig, 'displayName')}</th>
              <th className="show-on-desktop" onClick={() => requestSort('teamSalary')}>Salary {getSortIndicator(sortConfig, 'teamSalary')}</th>
              <th onClick={() => requestSort('cashes')}>Cashes {getSortIndicator(sortConfig, 'cashes')}</th>
              <th onClick={() => requestSort('winnings')}>Won {getSortIndicator(sortConfig, 'winnings')}</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((entrant) => {
              const isCurrentUser = entrant.username?.toLowerCase() === currentUsername?.toLowerCase();
              const userClass = isCurrentUser ? 'current-user-team' : '';
              // Use numericRank for money line check
              const isLastMoneyPlace = numPayingPlaces > 0 && entrant.numericRank === numPayingPlaces;
              const lastMoneyClass = isLastMoneyPlace ? 'last-money-row' : '';

              return (
                <tr
                  key={entrant._id}
                  onClick={() => onTeamSelect(entrant)}
                  className={`${userClass} ${lastMoneyClass}`.trim()}
                >
                  {/* Display the place string (e.g., "1", "T3") */}
                  <td>{entrant.place}</td> 
                  <td>
                    {entrant.displayName}
                  </td>
                  <td className="show-on-desktop">${formatNumber(entrant.teamSalary)}</td>
                  <td>${formatNumber(entrant.cashes)}</td>
                  <td>${formatNumber(entrant.winnings)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Updated PropTypes slightly - removed playerPayouts as it's not used directly
EntrantsTeamsPokerCompleted.propTypes = {
  contest: PropTypes.shape({
    entrants: PropTypes.arrayOf(PropTypes.shape({
      _id: PropTypes.string.isRequired,
      username: PropTypes.string.isRequired,
      entryName: PropTypes.string,
      cashes: PropTypes.number.isRequired,
      // Team details not strictly needed for this display, but good context
      team: PropTypes.shape({
        captain: PropTypes.string.isRequired,
        flexPlayers: PropTypes.arrayOf(PropTypes.string).isRequired
      }).isRequired,
      teamSalary: PropTypes.number
    })).isRequired,
    // Players array might still be useful for future features, but not used here
    players: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      finish: PropTypes.number, 
      cashedFor: PropTypes.number.isRequired
    })).isRequired, 
    entrantPayouts: PropTypes.array, // Still used for money line
  }).isRequired,
  currentUsername: PropTypes.string, // Updated prop type
  onTeamSelect: PropTypes.func.isRequired
};

export default EntrantsTeamsPokerCompleted;
